/**
 * <cart-gift-selector>
 *
 * Panel coulissant qui :
 * - S'ouvre quand on clique sur "Modifier" (event delegation sur document)
 * - Affiche les 3 cadeaux + carte refus
 * - Swap : retire l'ancien cadeau + ajoute le nouveau en une seule requête
 * - Refus : retire le cadeau + pose le flag localStorage.gift_refused
 *
 * Verrou partagé avec <cart-free-gift> (lock / release) pour que l'ajout
 * automatique ne réinjecte pas un cadeau pendant un échange ou un refus.
 */

const STORAGE_REFUSED_KEY = 'gift_refused';

class CartGiftSelector extends HTMLElement {
  #currentVariantId = 0;
  #abortController = null;

  connectedCallback() {
    this.SECTION_ID = this.dataset.sectionId || '';
    this.#abortController = new AbortController();

    // Event delegation : "Modifier" peut être recréé par AJAX
    document.addEventListener('click', this.#handleDocumentClick, {
      signal: this.#abortController.signal,
    });

    // Boutons internes au panel (stable)
    this.addEventListener('click', this.#handlePanelClick);

    // ESC pour fermer
    document.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  // ===== Event handlers =====

  #handleDocumentClick = (e) => {
    const openBtn = e.target.closest('[data-action="open-gift-panel"]');
    if (openBtn) {
      e.preventDefault();
      this.#currentVariantId = parseInt(openBtn.dataset.currentVariantId, 10) || 0;
      this.open();
    }
  };

  #handlePanelClick = (e) => {
    const closeBtn = e.target.closest('[data-action="close-gift-panel"]');
    if (closeBtn) {
      e.preventDefault();
      this.close();
      return;
    }
    const selectBtn = e.target.closest('[data-action="select-gift"]');
    if (selectBtn) {
      e.preventDefault();
      const variantId = parseInt(selectBtn.dataset.variantId, 10);
      if (variantId) this.swapGift(variantId, selectBtn);
      return;
    }
    const refuseBtn = e.target.closest('[data-action="refuse-gift"]');
    if (refuseBtn) {
      e.preventDefault();
      this.refuseGift(refuseBtn);
    }
  };

  #handleKeydown = (e) => {
    if (e.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  };

  // ===== Open / close =====

  open() {
    // Marque visuellement le cadeau actif
    this.querySelectorAll('[data-action="select-gift"]').forEach((btn) => {
      const id = parseInt(btn.dataset.variantId, 10);
      const isSelected = id === this.#currentVariantId;
      btn.classList.toggle('cart-gift-panel__select--selected', isSelected);
      btn.textContent = isSelected ? 'Sélectionné' : 'Sélectionner';
    });
    this.hidden = false;
    // Force reflow pour que la transition CSS s'applique
    void this.offsetWidth;
    this.classList.add('is-open');
  }

  close() {
    this.classList.remove('is-open');
    setTimeout(() => {
      this.hidden = true;
    }, 320);
  }

  // ===== Lecture du panier =====

  #freeGift() {
    return document.querySelector('cart-free-gift');
  }

  #giftIds() {
    return Array.from(this.querySelectorAll('[data-action="select-gift"][data-variant-id]'))
      .map((btn) => parseInt(btn.dataset.variantId, 10))
      .filter(Boolean);
  }

  /**
   * Le panier fait foi, pas la page : le « cadeau en cours » lu dans le DOM
   * (data-current-variant-id) peut être périmé, et un échange calculé dessus
   * ajoutait le nouveau cadeau sans retirer l'ancien.
   */
  async #giftLinesInCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!res.ok) throw new Error('Panier indisponible. Réessaye plus tard.');
    const cart = await res.json();
    const ids = this.#giftIds();
    return (cart.items || []).filter((item) => ids.includes(item.variant_id));
  }

  async #postUpdates(updates) {
    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: this.SECTION_ID }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Le cadeau choisi est indisponible. Réessaye plus tard.');
    }
    return res.json();
  }

  // ===== Swap & refuse =====
  // Le verrou de <cart-free-gift> empêche l'ajout automatique d'interférer
  // pendant l'opération ; tout changement de panier survenu entre-temps est
  // réévalué à la libération.

  async swapGift(newVariantId, btn) {
    localStorage.removeItem(STORAGE_REFUSED_KEY);
    const freeGift = this.#freeGift();
    freeGift?.lock();
    if (btn) btn.disabled = true;
    this.#hideError();
    let wrote = false;

    try {
      const lines = await this.#giftLinesInCart();
      const alreadyChosen =
        lines.length === 1 && lines[0].variant_id === newVariantId && lines[0].quantity === 1;
      if (alreadyChosen) {
        this.close();
        return;
      }

      // Une seule requête : TOUS les cadeaux présents à 0, le nouveau à 1.
      const updates = {};
      for (const line of lines) updates[line.variant_id] = 0;
      updates[newVariantId] = 1;

      const data = await this.#postUpdates(updates);
      wrote = true;
      this.#currentVariantId = newVariantId;
      this.#refreshDrawer(data);
      this.close();
    } catch (e) {
      this.#showError(e.message || 'Le cadeau choisi est indisponible. Réessaye plus tard.');
    } finally {
      if (btn) btn.disabled = false;
      freeGift?.release({ settle: wrote });
    }
  }

  async refuseGift(btn) {
    localStorage.setItem(STORAGE_REFUSED_KEY, 'true');
    const freeGift = this.#freeGift();
    freeGift?.lock();
    if (btn) btn.disabled = true;
    this.#hideError();
    let wrote = false;

    try {
      const lines = await this.#giftLinesInCart();
      // Pas de cadeau à retirer (faux item) : le refus est noté, on ferme.
      if (!lines.length) {
        this.close();
        return;
      }

      const data = await this.#postUpdates(Object.fromEntries(lines.map((line) => [line.key, 0])));
      wrote = true;
      this.#currentVariantId = 0;
      this.#refreshDrawer(data);
      this.close();
    } catch (e) {
      this.#showError(e.message || 'Erreur. Réessaye plus tard.');
    } finally {
      if (btn) btn.disabled = false;
      freeGift?.release({ settle: wrote });
    }
  }

  // ===== Error display =====

  #ensureErrorEl() {
    let el = this.querySelector('.cart-gift-panel__error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'cart-gift-panel__error';
      el.setAttribute('role', 'alert');
      el.hidden = true;
      const products = this.querySelector('.cart-gift-panel__products');
      if (products) products.prepend(el);
    }
    return el;
  }

  #showError(msg) {
    const el = this.#ensureErrorEl();
    el.textContent = msg;
    el.hidden = false;
  }

  #hideError() {
    const el = this.querySelector('.cart-gift-panel__error');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  // ===== Refresh =====

  #refreshDrawer(cartData) {
    const sectionHtml = cartData?.sections?.[this.SECTION_ID];
    if (sectionHtml) {
      this.#refreshDrawerFromHtml(sectionHtml);
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cartData, sourceId: 'cart-gift-selector' },
      })
    );
  }

  #refreshDrawerFromHtml(html) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartItems = doc.querySelector('cart-items-component');
      const oldCartItems = document.querySelector(
        `cart-items-component[data-section-id="${this.SECTION_ID}"]`
      );
      if (newCartItems && oldCartItems) {
        oldCartItems.innerHTML = newCartItems.innerHTML;
      }
    } catch (e) {}
  }
}

if (!customElements.get('cart-gift-selector')) {
  customElements.define('cart-gift-selector', CartGiftSelector);
}
